// The processing report download (TTTL-220). The engine generates a
// per-pipeline record from the lineage ledger; the page only hands the
// generated document over — what to ask for, what to call the file, and
// how a refusal reads live here, the fetch and the modal elsewhere.

// The three renderings the engine offers (mstream 0.140, docs/reports.md):
// CSV is the artefact somebody forwards, text reads inline, JSON is the
// same record for a machine. Any other value is a 400 naming these three.
export type ReportFormat = "csv" | "text" | "json";

export interface ReportOptions {
  format: ReportFormat;
  /** The moment the report is about (RFC 3339); now when absent. One knob
   *  moves everything: the description in force, what had been watched,
   *  and the version of the rules that classifies the fields. A future
   *  moment is refused by the engine. */
  reportedFor?: string;
}

/** The engine path for the chosen report, ready for the API client. */
export function reportPath(pipeline: string, options: ReportOptions): string {
  const params = new URLSearchParams({ format: options.format });
  if (options.reportedFor) params.set("reported_for", options.reportedFor);
  return `/reports/pipelines/${encodeURIComponent(pipeline)}?${params}`;
}

const EXTENSIONS: Record<ReportFormat, string> = {
  csv: "csv",
  text: "txt",
  json: "json",
};
const MIME_TYPES: Record<ReportFormat, string> = {
  csv: "text/csv",
  text: "text/plain",
  json: "application/json",
};

/** The saved file's media type. */
export function reportMimeType(format: ReportFormat): string {
  return MIME_TYPES[format];
}

/** `{pipeline}-report-{date}.csv` / `.txt` / `.json` — the report's own
 *  date when a moment was chosen, today's otherwise. */
export function reportFilename(
  pipeline: string,
  format: ReportFormat,
  reportedFor?: string,
): string {
  const date = (reportedFor ?? new Date().toISOString()).slice(0, 10);
  return `${pipeline}-report-${date}.${EXTENSIONS[format]}`;
}

/** Whether the chosen moment is ahead of the clock: the engine refuses a
 *  report about a moment that has not happened. */
export function isFutureMoment(moment: Date | undefined, now: Date): boolean {
  return moment !== undefined && moment.getTime() > now.getTime();
}

export const FUTURE_MOMENT =
  "That moment hasn't happened yet. Pick a date and time in the past, or leave the field empty for the current state.";
const NOTHING_RECORDED = "No record exists for this pipeline on that date.";
const FALLBACK = "The report could not be downloaded.";

/** How a failed download reads: a 404 means the ledger holds nothing for
 *  the pipeline at that moment, which is an answer, not a broken file; a
 *  400 is the engine refusing a moment ahead of its clock (the page
 *  guards against that, so this covers a browser clock running ahead). */
export function reportProblem(error: unknown): string {
  if (error instanceof Error) {
    const status = "status" in error ? error.status : undefined;
    if (status === 404) return NOTHING_RECORDED;
    if (status === 400) return FUTURE_MOMENT;
    // An error with an empty body would otherwise read as an empty toast.
    return error.message || FALLBACK;
  }
  return FALLBACK;
}
